import { useEffect, useState } from "react";

import { ArrowButton, AutocompleteMultipleSelect, ColumnSortEnum, Select, TableMetadata, TableStateWithFilters, Typography, useTable } from "@king-ict/eupisi-ui/components";
import { CodeBook, ExportTypeEnum, PagedResult } from "@king-ict/eupisi-ui/models";
import { codeBookToSelectData, nameof } from "@king-ict/eupisi-ui/utils";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";

import { searchTowns } from "../../api/Geography/GeographyApi";
import {
  getInstitutionListData,
  searchInstitutionFilteredNew,
  searchInstitutionNew
} from "../../api/Institution/InstitutionApi";
import { ContentArea, CountySelect, ListTable, SearchCard, ToolbarTop } from "../../components";
import { useAuth } from "../../hooks/auth/useAuth";
import { Institution, InstitutionSearch, 
  InstitutionSort, InstitutionCodeBookSearch,
  InstitutionCodeBook
} from "../../models/Institution";
import { GrandParentCodeBookPagedFilter, GreatGrandParentCodeBookPagedFilter, HierarchicalCodeBook, } from "../../models/Shared";
import { ClaimType } from "../../models/User";
import { useGetProgramsQuery } from "../../queries/codeBookQuery";
import { institutionDetailsUrl } from "../../routes/routes";
import { TableUtils } from "../../utils";

export function InstitutionList() {
  const { t } = useTranslation();
  const history = useHistory();
  const auth = useAuth();
  //STORAGE KEY
  const storageKey = 'InstitutionListSearchTableState';
  //INIT TABLE STATE WITH FILTERS - could be temporary stored in local session storage
  const createInitialTableState = () => {
    return TableUtils.CreateInitialTableState<InstitutionSearch>(
      storageKey,
      TableUtils.tableDefaultState(InstitutionSort.Institution, ColumnSortEnum.ASC)
    )
  }
  const [initialTableState] = useState<TableStateWithFilters<InstitutionSearch>>(() => createInitialTableState());
  //INIT SEARCH FILTER
  const createInitialInstitutionSearchNew = (): InstitutionSearch => {
    return {
      countyId: auth.currentRole?.claimType === ClaimType.County && auth.currentRole.locations && auth.currentRole.locations.length > 0 ? auth.currentRole.locations[0] : undefined,
      townIds: initialTableState.townIds,
      institutionIds:
        initialTableState.institutionIds
          ? initialTableState.institutionIds
          : defaultInstitutionIds,
      programIds: initialTableState.programIds,
      excludeHandicapped: initialTableState.excludeHandicapped,
      excludeSatellite: initialTableState.excludeSatellite,
    }
  }
  const defaultInstitutionIds =
    auth.currentRole?.claimType === ClaimType.Institution
      && (auth.currentRole?.locations?.length ?? 0) > 0
      && auth.currentRole?.locations
      ? auth.currentRole.locations
      : [] as number[]
  //TABLE DATA
  const defaultTableData = TableUtils.tableDefaultData<Institution>()
  const [tableData, setTableData] = useState<PagedResult<Institution>>(defaultTableData);
  //SET SEARCH FILTERS
  const [institutionSearch, setInstitutionSearch] = useState<InstitutionSearch>(createInitialInstitutionSearchNew());
  const [tableSearch, setTableSearch] = useState<InstitutionSearch>(() => createInitialInstitutionSearchNew());
  const [filterChanged, setFilterChanged] = useState<boolean>(false);
  const [clearFilter, setClearFilter] = useState<boolean>(true);
  //TABLE METADATA
  const tableMetadata: TableMetadata<Institution> = {
    rowKey: "id",
    columns: [
      {
        columnKey: "code",
        header: t("common.institutionList.programCode"),
        accessor: "code",
        sortBy: InstitutionSort.Code
      },
      {
        columnKey: "town",
        header: t("common.institutionList.townName"),
        accessor: (item: Institution) => {
          return (
            <div>
              {item.address?.town}
            </div>)
        },
        sortBy: InstitutionSort.Town
      },
      {
        columnKey: "institutionName",
        header: t("common.institutionList.institutionName"),
        accessor: "name",
        sortBy: InstitutionSort.Institution
      },
      {
        columnKey: "institutionAddress",
        header: t("common.institutionList.institutionAddress"),
        accessor: (item: Institution) => {
          return (
            <div>
              {item.address?.street}
            </div>)
        },
        sortBy: InstitutionSort.AdresaSjedista
      },
      {
        columnKey: "program",
        header: t("common.institutionList.programName"),
        accessor: (item: Institution) => {
          return (
            <>
              <ul>
                {item.programs.map((oneProgram) => (
                  <li>
                    <Typography component="span" variant="small">
                      {oneProgram.name}
                    </Typography>
                  </li>
                ))}
              </ul>

            </>
          )
        },
        sortBy: InstitutionSort.Program
      },
      {
        columnKey: "details",
        header: "",
        accessor: (item: Institution) => {
          return (<div className={"text-right"}>
            <ArrowButton
              ariaLabel={t("common.institutionList.details")}
              onClick={() =>
                handleDetailsClicked(item.id)
              } />
          </div>)
        }
      }
    ],
    rowToolTip: () => undefined
  };
  //SET TABLE
  var [tableProps, tableState, setFilters] = useTable<InstitutionSearch>(
    {
      metadata: tableMetadata,
      data: tableData!.results,
      pagination: true,
      paginationPage: initialTableState.page,
      paginationPageSize: initialTableState.pageSize as any,
      totalItems: tableData!.rowCount,
      defaultSort: { order: initialTableState.order, sortBy: initialTableState.sortBy }
    }
  );
  //FILTER DATA
  const [filteredTowns, setFilteredTowns] = useState<CodeBook[]>([] as CodeBook[]);
  // const [filteredPrograms, setFilteredPrograms] = useState<CodeBook[]>([] as CodeBook[]);
  const [filteredInstitutions, setFilteredInstitutions] = useState<InstitutionCodeBook[]>([]);
  // const [institutions, setInstitutions] = useState<PagedResult<Institution>>(createDefaultTableData());
  const { data: programs } = useGetProgramsQuery();


  useEffect(() => {
    (async () => {
      var storage = sessionStorage.getItem(storageKey);
      if (storage) {
        sessionStorage.removeItem(storageKey);
        if ((institutionSearch.townIds?.length ?? 0) > 0)
          handleTownFilterChange("");
        if ((institutionSearch.institutionIds?.length ?? 0) > 0)
          handleInstitutionFilterChange("");
      } else if ((institutionSearch.institutionIds?.length ?? 0) > 0) {
        handleInstitutionFilterChange("");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (clearFilter) {
      setClearFilter(false);
      setFilterChanged(false);
    }
    else
      setFilterChanged(true);
    TableUtils.RemoveSessionStorageItem(storageKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [institutionSearch]);

  useEffect(() => {
    (async () => {
      var request = TableUtils.GetSearchRequest<InstitutionSearch>(tableSearch, tableState);
      var itemList = await searchInstitutionFilteredNew(request);
      if (itemList !== null && itemList.results.length > 0) {
        setTableData(itemList);
      }
      else {
        setTableData(defaultTableData);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableState]);

  const handleDetailsClicked = (id?: number) => {
    if (id) {
      TableUtils.SetSessionStorageItem<InstitutionSearch>(storageKey, tableSearch, tableState);
      history.push(institutionDetailsUrl(id!));
    }
  };
  const handleDownloadClicked = async (exportType: ExportTypeEnum) => {
    await getInstitutionListData(institutionSearch, exportType);
  };
  const search = async () => {
    setFilters({ ...tableSearch });
  };

  const handleCountyChange = (e: any) => {
    const countyId = Number.parseInt(e.target.value);

    const newInstitutionSearch: InstitutionSearch = {
      ...institutionSearch,
      countyId: !isNaN(countyId) ? countyId : undefined,
    };
    setInstitutionSearch(newInstitutionSearch);
  };
  const handleTownFilterChange = async (filter: string) => {
    const searchTown: GrandParentCodeBookPagedFilter = {
      pageSize: 500,
      page: 1,
      ids: institutionSearch.townIds,
      search: filter,
      grandParentId: institutionSearch.countyId ? [institutionSearch.countyId] : [],
    };
    var result = await searchTowns(searchTown);
    if (result && result.results)
      setFilteredTowns(result.results);
  }
  const handleInstitutionFilterChange = async (filter: string) => {
    const search: InstitutionCodeBookSearch = {
      search: filter,
      onlyActive: false,
      includeOutsideEnrollmentArea: true,
      includeHandicapped: undefined, // both
      includePrivate: undefined, // both
      includeSatellite: undefined, // both
      ids: auth.currentRole?.claimType === ClaimType.Institution ? auth.currentRole.locations : institutionSearch.institutionIds,
      townIds: institutionSearch.townIds,
      countyIds: institutionSearch.countyId ? [institutionSearch.countyId] : [],
    };
    var result = await searchInstitutionNew(search);
    setFilteredInstitutions(result);
  }
  const handleTownChange = (e: any) => {
    const newTowns = e.target.value as CodeBook[];
    const newInstitutions = filteredInstitutions.filter(val => institutionSearch.institutionIds.find(v => val.id === v) !== undefined)
      .filter(val => newTowns.find(v => v.id === val.townId) !== undefined);

    const newInstitutionSearch: InstitutionSearch = {
      ...institutionSearch,
      townIds: newTowns ? newTowns.map(val => val.id) : [],
      institutionIds: newInstitutions ? newInstitutions.map(val => val.id) : [],
    };
    setInstitutionSearch(newInstitutionSearch);
  }
  const handleProgramChange = (e: any) => {
    const newPrograms = e.target.value as number[];

    const newInstitutionSearch: InstitutionSearch = {
      ...institutionSearch,
      programIds: newPrograms
    };
    setInstitutionSearch(newInstitutionSearch);
  }
  const handleInstitutionChange = (e: any) => {
    const newInstitutions = e.target.value as CodeBook[];
    handleInstitutionChangeNumbers(newInstitutions.map(val => val.id));
  }
  const handleInstitutionChangeNumbers = (vals: number[]) => {

    const newInstitutionSearch: InstitutionSearch = {
      ...institutionSearch,
      institutionIds: vals,
    };
    setInstitutionSearch(newInstitutionSearch);
  };

  return (
    <ContentArea>
      <ToolbarTop
        pageTitle={t("common.institutionList.institutionList")}
      ></ToolbarTop>
      <SearchCard
        onClickSearch={() => {
          setTableSearch(institutionSearch)
          setFilterChanged(false);
          search();
        }}
        onClickClearSearch={() => {
          setClearFilter(true);
          setInstitutionSearch({ institutionIds: defaultInstitutionIds, programIds: [], townIds: [], excludeHandicapped: false, excludeSatellite: false });
          setTableSearch({ institutionIds: defaultInstitutionIds, programIds: [], townIds: [], excludeHandicapped: false, excludeSatellite: false })
          search();
        }}
        isFilterChanged={filterChanged}
        showSearch={auth.currentRole?.claimType !== ClaimType.Institution || (institutionSearch.institutionIds && institutionSearch.institutionIds.length > 0) ? "show" : "disable"}
      >
        {(auth.currentRole?.claimType !== ClaimType.Institution) &&
          <>
            <Row>
              <Col md={4}>
                <CountySelect
                  label={t("common.county")}
                  name={nameof<typeof institutionSearch>("countyId")}
                  value={institutionSearch.countyId}
                  onChange={handleCountyChange}
                  countyIds={auth.currentRole?.claimType === ClaimType.County ? auth.currentRole.locations : undefined}
                />
              </Col>
            </Row>
            <Row >
              <Col md={4}>
                <AutocompleteMultipleSelect<CodeBook>
                  label={t("common.town")}
                  name={nameof<typeof institutionSearch>("townIds")}
                  value={institutionSearch?.townIds && institutionSearch.townIds.length > 0 ? filteredTowns.filter(val => institutionSearch.townIds.find(v => v === val.id) !== undefined) : []}
                  valueProp="id"
                  textProp="name"
                  onChange={handleTownChange}
                  selectOptions={filteredTowns}
                  onFilterChange={handleTownFilterChange}
                  maximumSelectedItems={10}
                />
              </Col>
              <Col md={7}>
                <AutocompleteMultipleSelect<HierarchicalCodeBook>
                  label={t("common.institution")}
                  name={nameof<typeof institutionSearch>("institutionIds")}
                  value={institutionSearch.institutionIds.length > 0 ? filteredInstitutions.filter(val => institutionSearch.institutionIds.find(v => v === val.id) !== undefined) : []}
                  valueProp="id"
                  textProp="name"
                  onChange={handleInstitutionChange}
                  selectOptions={filteredInstitutions}
                  onFilterChange={handleInstitutionFilterChange}
                  maximumSelectedItems={10}
                />
              </Col>
            </Row>
          </>
        }
        {(auth.currentRole?.claimType === ClaimType.Institution) &&
          <Row>
            <Col md={12}>
              <Select
                multiple
                label={t("common.institution")}
                name={nameof<typeof institutionSearch>("institutionIds")}
                value={institutionSearch.institutionIds}
                onChange={(e) => handleInstitutionChangeNumbers(e.target.value)}
                selectData={codeBookToSelectData(filteredInstitutions)}
              />
            </Col>
          </Row>
        }
        <Row >
          <Col md={8}>
            <Select
              multiple
              label={t("common.programs")}
              name={nameof<typeof institutionSearch>("programIds")}
              value={institutionSearch.programIds}
              onChange={handleProgramChange}
              selectData={codeBookToSelectData(programs)}
            />
          </Col>
        </Row>
      </SearchCard>

      <div>
        <ListTable
          tableProps={tableProps}
          onClickDownload={(exportType: ExportTypeEnum) => handleDownloadClicked(exportType)}
          showDownload
        />
      </div>
    </ContentArea>
  )
}

